import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Calendar, Clock, ChevronRight, ChevronLeft, User, Tag, Share2, Facebook, Twitter, Linkedin, Link as LinkIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Breadcrumbs with Schema.org markup
function Breadcrumbs({ items }) {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <nav aria-label="Breadcrumb" className="text-sm text-secondary mb-6">
        <ol className="flex items-center gap-2 flex-wrap">
          {items.map((item) => (
            <li key={`breadcrumb-${item.path}`} className="flex items-center gap-2">
              {item.position > 1 && <ChevronRight className="h-3 w-3" />}
              {item.current ? (
                <span className="text-primary font-medium truncate max-w-[200px]">{item.name}</span>
              ) : (
                <Link to={item.path} className="hover:text-gold transition-colors" style={{ color: 'inherit' }}>
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </nav>
    </>
  );
}

// Social share buttons
function SocialShare({ title, url }) {
  const { t } = useTranslation();
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const shareLinks = [
    {
      name: 'Facebook',
      icon: Facebook,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      color: '#1877F2'
    },
    {
      name: 'Twitter',
      icon: Twitter,
      url: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
      color: '#1DA1F2'
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      color: '#0A66C2'
    }
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('common.copied') || '¡Copiado!');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-secondary flex items-center gap-1">
        <Share2 className="h-4 w-4" />
        {t('share.share') || 'Compartir'}:
      </span>
      {shareLinks.map(link => (
        <a
          key={link.name}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="w-8 h-8 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
          style={{ backgroundColor: link.color }}
          title={link.name}
        >
          <link.icon className="h-4 w-4 text-white" />
        </a>
      ))}
      <button
        onClick={copyLink}
        className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors"
        title={t('common.copyLink') || 'Copiar enlace'}
      >
        <LinkIcon className="h-4 w-4 text-gray-600" />
      </button>
    </div>
  );
}

export default function BlogArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedArticles, setRelatedArticles] = useState([]);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/blog/article/${slug}`, {
          params: { lang: language }
        });
        setArticle(res.data);
        
        // Fetch related articles from same category
        if (res.data.category_id) {
          const relatedRes = await axios.get(`${API_URL}/api/blog/articles`, {
            params: { 
              lang: language,
              category: res.data.category?.slug,
              limit: 3
            }
          });
          setRelatedArticles(
            (relatedRes.data.articles || []).filter(a => a.id !== res.data.id).slice(0, 2)
          );
        }
      } catch (error) {
        console.error('Error fetching article:', error);
        if (error.response?.status === 404) {
          navigate('/blog');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchArticle();
  }, [slug, language, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream py-8 px-4" style={{ backgroundColor: '#FDFBF7' }}>
        <div className="max-w-3xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/3" />
            <div className="h-10 bg-gray-200 rounded w-3/4" />
            <div className="h-64 bg-gray-200 rounded" />
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 bg-gray-200 rounded w-2/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-cream py-16 px-4 text-center" style={{ backgroundColor: '#FDFBF7' }}>
        <span className="text-6xl mb-4 block">📝</span>
        <h1 className="text-2xl font-bold text-primary mb-2">
          {language === 'es' && 'Artículo no encontrado'}
          {language === 'en' && 'Article not found'}
          {language === 'it' && 'Articolo non trovato'}
        </h1>
        <Link to="/blog">
          <Button className="mt-4" style={{ backgroundColor: '#D4A574' }}>
            {language === 'es' && 'Volver al blog'}
            {language === 'en' && 'Back to blog'}
            {language === 'it' && 'Torna al blog'}
          </Button>
        </Link>
      </div>
    );
  }

  const title = article.title?.[language] || article.title?.es || '';
  const content = article.content?.[language] || article.content?.es || '';
  const excerpt = article.excerpt?.[language] || article.excerpt?.es || '';
  const metaDescription = article.meta_description?.[language] || article.meta_description?.es || excerpt;
  const keywords = article.keywords?.[language] || article.keywords?.es || [];
  const categoryName = article.category?.name?.[language] || article.category?.name?.es || '';
  const categorySlug = article.category?.slug || '';
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'es' ? 'es-ES' : language === 'it' ? 'it-IT' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const currentUrl = `https://tantra-map.com/blog/${slug}`;
  
  const breadcrumbs = [
    { name: t('nav.home') || 'Inicio', path: '/', url: 'https://tantra-map.com/', position: 1 },
    { name: 'Blog', path: '/blog', url: 'https://tantra-map.com/blog', position: 2 },
    { name: title, path: `/blog/${slug}`, url: currentUrl, current: true, position: 3 }
  ];

  // Article Schema.org JSON-LD
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": metaDescription,
    "image": article.featured_image || "https://tantra-map.com/default-blog-image.jpg",
    "author": {
      "@type": "Person",
      "name": article.author_name || "Tantra Map"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Tantra Map",
      "logo": {
        "@type": "ImageObject",
        "url": "https://tantra-map.com/logo.png"
      }
    },
    "datePublished": article.published_at,
    "dateModified": article.updated_at,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": currentUrl
    },
    "keywords": keywords.join(", "),
    "articleSection": categoryName,
    "wordCount": content.split(/\s+/).length,
    "inLanguage": language
  };

  return (
    <>
      <Helmet>
        <title>{`${title || 'Artículo'} | Tantra Map Blog`}</title>
        <meta name="description" content={metaDescription || ''} />
        <meta name="keywords" content={Array.isArray(keywords) ? keywords.join(", ") : ''} />
        
        {/* Open Graph */}
        <meta property="og:title" content={title || 'Tantra Map Blog'} />
        <meta property="og:description" content={metaDescription || ''} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={currentUrl} />
        {article.featured_image && <meta property="og:image" content={article.featured_image} />}
        <meta property="article:published_time" content={article.published_at || ''} />
        <meta property="article:modified_time" content={article.updated_at || ''} />
        <meta property="article:section" content={categoryName || ''} />
        {Array.isArray(keywords) && keywords.map((kw) => (
          <meta key={`meta-tag-${kw}`} property="article:tag" content={kw} />
        ))}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title || 'Tantra Map Blog'} />
        <meta name="twitter:description" content={metaDescription || ''} />
        {article.featured_image && <meta name="twitter:image" content={article.featured_image} />}
        
        {/* Canonical */}
        <link rel="canonical" href={currentUrl} />
        
        {/* Alternate languages */}
        {article.slug?.es && <link rel="alternate" hrefLang="es" href={`https://tantra-map.com/blog/${article.slug.es}`} />}
        {article.slug?.en && <link rel="alternate" hrefLang="en" href={`https://tantra-map.com/blog/${article.slug.en}`} />}
        {article.slug?.it && <link rel="alternate" hrefLang="it" href={`https://tantra-map.com/blog/${article.slug.it}`} />}
        
        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      </Helmet>

      <article className="min-h-screen bg-cream py-8 px-4" style={{ backgroundColor: '#FDFBF7' }} data-testid="blog-article-page">
        <div className="max-w-3xl mx-auto">
          <Breadcrumbs items={breadcrumbs} />
          
          {/* Category badge */}
          {categoryName && (
            <Link 
              to={`/blog?category=${categorySlug}`}
              className="inline-block px-3 py-1 text-sm font-medium rounded-full mb-4 hover:opacity-80 transition-opacity"
              style={{ backgroundColor: '#D4A574', color: 'white' }}
            >
              {categoryName}
            </Link>
          )}
          
          {/* Title */}
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4 leading-tight">
            {title}
          </h1>
          
          {/* Meta info */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-secondary mb-6 pb-6 border-b">
            {article.author_name && (
              <span className="flex items-center gap-1">
                <User className="h-4 w-4" />
                {article.author_name}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {formatDate(article.published_at)}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {article.reading_time} min {language === 'es' ? 'de lectura' : language === 'it' ? 'di lettura' : 'read'}
            </span>
          </div>
          
          {/* Featured image */}
          {article.featured_image && (
            <div className="rounded-xl overflow-hidden mb-8 shadow-lg">
              <img 
                src={article.featured_image} 
                alt={title}
                className="w-full h-auto"
              />
            </div>
          )}
          
          {/* Content */}
          <article 
            className="prose prose-lg max-w-none mb-8"
            style={{
              '--tw-prose-body': '#4a4a4a',
              '--tw-prose-headings': '#2d2d2d',
              '--tw-prose-links': '#D4A574',
              '--tw-prose-bold': '#2d2d2d',
              '--tw-prose-bullets': '#D4A574',
              '--tw-prose-quotes': '#4a4a4a',
              '--tw-prose-quote-borders': '#D4A574',
            }}
          >
            <style>{`
              .blog-content section {
                margin-bottom: 2.5rem;
              }
              .blog-content h2 {
                font-family: 'Playfair Display', Georgia, serif;
                font-size: 1.75rem;
                font-weight: 700;
                color: #2d2d2d;
                margin-top: 2rem;
                margin-bottom: 1rem;
                padding-bottom: 0.5rem;
                border-bottom: 2px solid #D4A574;
              }
              .blog-content h3 {
                font-family: 'Playfair Display', Georgia, serif;
                font-size: 1.35rem;
                font-weight: 600;
                color: #3d3d3d;
                margin-top: 1.5rem;
                margin-bottom: 0.75rem;
              }
              .blog-content p {
                color: #4a4a4a;
                line-height: 1.8;
                margin-bottom: 1.25rem;
                text-align: justify;
              }
              .blog-content ul, .blog-content ol {
                margin: 1.5rem 0;
                padding-left: 1.5rem;
              }
              .blog-content li {
                color: #4a4a4a;
                line-height: 1.7;
                margin-bottom: 0.5rem;
              }
              .blog-content li::marker {
                color: #D4A574;
              }
              .blog-content strong {
                color: #2d2d2d;
                font-weight: 600;
              }
              .blog-content blockquote {
                border-left: 4px solid #D4A574;
                padding-left: 1.5rem;
                margin: 2rem 0;
                font-style: italic;
                color: #5a5a5a;
                background: linear-gradient(to right, rgba(212, 165, 116, 0.1), transparent);
                padding: 1rem 1.5rem;
                border-radius: 0 8px 8px 0;
              }
              .blog-content a {
                color: #D4A574;
                text-decoration: none;
                border-bottom: 1px solid transparent;
                transition: border-color 0.2s;
              }
              .blog-content a:hover {
                border-bottom-color: #D4A574;
              }
              .blog-content img {
                border-radius: 12px;
                margin: 2rem 0;
              }
              .blog-content hr {
                border: none;
                height: 2px;
                background: linear-gradient(to right, transparent, #D4A574, transparent);
                margin: 2.5rem 0;
              }
            `}</style>
            <div 
              className="blog-content"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
            />
          </article>
          
          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-6 pb-6 border-b">
              <Tag className="h-4 w-4 text-secondary" />
              {article.tags.map((tag) => (
                <span 
                  key={`tag-${tag}`}
                  className="px-3 py-1 bg-gray-100 rounded-full text-sm text-secondary"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
          
          {/* Share */}
          <div className="mb-8 pb-8 border-b">
            <SocialShare title={title} url={currentUrl} />
          </div>
          
          {/* Related articles */}
          {relatedArticles.length > 0 && (
            <div className="mb-8">
              <h3 className="font-serif text-xl font-bold text-primary mb-4">
                {language === 'es' && 'Artículos relacionados'}
                {language === 'en' && 'Related articles'}
                {language === 'it' && 'Articoli correlati'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {relatedArticles.map(related => (
                  <Link
                    key={related.id}
                    to={`/blog/${related.slug?.[language] || related.slug?.es}`}
                    className="bg-white rounded-lg p-4 hover:shadow-md transition-shadow group"
                  >
                    <h4 className="font-medium text-primary group-hover:text-gold transition-colors line-clamp-2">
                      {related.title?.[language] || related.title?.es}
                    </h4>
                    <p className="text-sm text-secondary mt-1 line-clamp-2">
                      {related.excerpt?.[language] || related.excerpt?.es}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          )}
          
          {/* Back to blog */}
          <Link to="/blog">
            <Button variant="outline" className="flex items-center gap-2">
              <ChevronLeft className="h-4 w-4" />
              {language === 'es' && 'Volver al blog'}
              {language === 'en' && 'Back to blog'}
              {language === 'it' && 'Torna al blog'}
            </Button>
          </Link>
        </div>
      </article>
    </>
  );
}
